import { useMutation, useQueryClient } from "@tanstack/react-query";
import { UserProfileDto, UserRole } from "@green0meter/api-client";
import { FieldValues, UseFormSetError } from "react-hook-form";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";

import { DOMAIN_COMPANIES } from "../domain";
import { ACTION_GET_COMPANIES } from "../GetCompanies";
import { ACTION_GET_ASSIGNED_COMPANIES } from "../GetAssignedCompanies/useGetAssignedCompanies";

import { companiesApi } from "@api/services";
import { roleGuard } from "@api";

export const ACTION_REMOVE_COMPANY = "REMOVE_COMPANY";

const allowedRoles = [UserRole.Owner, UserRole.Manager];

const removeCompany = async (companyId: string): Promise<void> => {
  await companiesApi.companiesControllerRemoveCompany(companyId);
};

export const useRemoveCompany = (
  user: UserProfileDto | null,
  setError?: UseFormSetError<FieldValues>,
): {
  removeCompany: (companyId: string) => void;
  removeCompanyAsync: (companyId: string) => Promise<void>;
  removeCompanyEnabled: boolean;
  isSuccessRemoveCompany: boolean;
  isPendingRemoveCompany: boolean;
} => {
  const enabled = roleGuard(user, allowedRoles);
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { mutate, mutateAsync, isSuccess, isPending } = useMutation({
    mutationKey: [DOMAIN_COMPANIES, ACTION_REMOVE_COMPANY],
    mutationFn: enabled ? removeCompany : undefined,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [DOMAIN_COMPANIES, ACTION_GET_COMPANIES],
      });
      queryClient.invalidateQueries({
        queryKey: [DOMAIN_COMPANIES, ACTION_GET_ASSIGNED_COMPANIES],
      });
    },
    onError: (error: AxiosError) => {
      if (setError) {
        const apiError = error as {
          response?: { data?: { code?: string; message?: string } };
        };

        const code = apiError?.response?.data?.code;

        if (code === "company_already_exists") {
          setError("vat", {
            message: t(`organization.errors.${code}`),
            type: "server",
          });
        }
      }
    },
  });

  return {
    removeCompany: mutate,
    removeCompanyAsync: mutateAsync,
    removeCompanyEnabled: enabled,
    isSuccessRemoveCompany: isSuccess,
    isPendingRemoveCompany: isPending,
  };
};
